/** Milliseconds before access-token expiry to trigger a proactive refresh. */
export const ACCESS_TOKEN_REFRESH_BUFFER_MS = 90_000; // 1.5 minutes (within 1–2 min of 15m TTL)

function base64UrlToJson(segment: string): string {
  const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
  const pad = normalized.length % 4 === 0 ? '' : '='.repeat(4 - (normalized.length % 4));
  const b64 = normalized + pad;

  const atobFn = (globalThis as typeof globalThis & { atob?: (data: string) => string }).atob;
  if (typeof atobFn === 'function') {
    return atobFn(b64);
  }

  const Buf = (globalThis as typeof globalThis & {
    Buffer?: { from: (data: string, enc: string) => { toString: (enc: string) => string } };
  }).Buffer;
  if (Buf?.from) {
    return Buf.from(b64, 'base64').toString('utf8');
  }

  throw new Error('No base64 decoder available');
}

/**
 * Read `exp` (ms since epoch) from a JWT payload without verifying the signature.
 * Returns null if the token is malformed or missing exp.
 */
export function getJwtExpiryMs(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2 || !parts[1]) return null;

    const payload = JSON.parse(base64UrlToJson(parts[1])) as { exp?: unknown };
    if (typeof payload.exp !== 'number' || !Number.isFinite(payload.exp)) return null;
    return payload.exp * 1000;
  } catch {
    return null;
  }
}

/** True when the access token is expired or within the proactive refresh buffer. */
export function shouldRefreshAccessToken(
  accessToken: string | null | undefined,
  now = Date.now(),
  bufferMs = ACCESS_TOKEN_REFRESH_BUFFER_MS,
): boolean {
  if (!accessToken) return false;
  const expMs = getJwtExpiryMs(accessToken);
  if (expMs == null) return false;
  return expMs - now <= bufferMs;
}
